<template>
  <div class="course-card" :class="{ 'course-card--no-access': !hasAccess }">
    <div class="course-header-wrap">
      <button class="course-header" :aria-expanded="expanded" @click="$emit('toggle', course.enrollId)">
        <i class="material-icons chevron" aria-hidden="true">{{ expanded ? 'expand_more' : 'chevron_right' }}</i>
        <div class="course-icon" :class="{ 'course-icon--no-access': !hasAccess }" :style="iconStyle">
          <img v-if="course.image" :src="course.image" :alt="course.name" />
          <span v-else aria-hidden="true">🎓</span>
        </div>
        <div class="course-info">
          <p class="course-name">{{ course.name }}</p>
          <span v-if="!hasAccess" class="no-access-badge">
            <i class="material-icons">block</i>
            Acesso suspenso
          </span>
          <p class="course-meta">
            {{ visibleGroupsCount }} {{ groupsUnitLabel }}
            ·
            {{ visibleLearningObjectsCount }} {{ visibleLearningObjectsCount === 1 ? 'aula' : 'aulas' }}
            <template v-if="sizeLabel">
              ·
              <span class="course-meta-size" :style="{ color: $theme.primaryColor }">{{ sizeLabel }}</span>
            </template>
          </p>
        </div>
      </button>
      <button
        class="course-actions-btn"
        aria-label="Mais ações do curso"
        @click.stop="$emit('open-course-actions', { enrollId: course.enrollId, name: course.name, hasAccess })"
      >⋮</button>
    </div>
    <div v-show="expanded" class="groups">
      <DownloadedGroupRow
        v-for="(group, idx) in visibleGroups"
        :key="group.id"
        :group="group"
        :enroll-id="course.enrollId"
        :expanded="isGroupExpanded(group.id, idx)"
        :has-access="hasAccess"
        @toggle="toggleGroup(group.id, idx)"
        @play="$emit('play', $event)"
        @open-actions="$emit('open-actions', $event)"
        @open-group-actions="$emit('open-group-actions', $event)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DownloadedGroupRow from './DownloadedGroupRow.vue';
import { formatBytes } from '@/services/download/downloadHelpers';

export default {
  name: 'DownloadedCourseCard',
  components: { DownloadedGroupRow },
  props: {
    course: { type: Object, required: true },
    filter: { type: String, default: 'all' },
    expanded: { type: Boolean, default: false }
  },
  data() {
    // Tristate: chave presente → user já interagiu (valor explícito); ausente
    // → cai no default (primeiro grupo aberto). Sem isso, o fallback
    // `idx === 0` re-expandia o 1º grupo a cada toggle e o user ficava preso
    // sem conseguir colapsar.
    return { explicitExpansion: {} };
  },
  computed: {
    ...mapGetters('download', ['getLocalPosition']),
    // Acesso = enroll ainda existe em rootState.enroll.enrolls. false significa
    // que o backend revogou (matrícula cancelada/refund/expirada). Card aparece
    // mas com overlay visual + playback bloqueado pelo lease (já existente).
    // Default true mantém retrocompat se o getter for atualizado depois.
    hasAccess() {
      return this.course.hasAccess !== false;
    },
    iconStyle() {
      const c = this.$theme.primaryColor || '#ffffff';
      if (!c.startsWith('#')) {
        return { backgroundColor: 'rgba(255,255,255,0.06)', color: c };
      }
      const clean = c.replace('#', '');
      const full = clean.length === 3 ? clean.split('').map(x => x + x).join('') : clean;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      return {
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.12)`,
        color: c
      };
    },
    visibleGroups() {
      if (this.filter === 'all') return this.course.groups;
      return this.course.groups
        .map(group => ({
          ...group,
          learningObjects: group.learningObjects.filter(lo => this.learningObjectMatchesFilter(lo))
        }))
        .filter(group => group.learningObjects.length > 0);
    },
    visibleGroupsCount() {
      return this.visibleGroups.length;
    },
    visibleLearningObjectsCount() {
      return this.visibleGroups.reduce((acc, g) => acc + g.learningObjects.length, 0);
    },
    // Etiqueta do contador adapta ao kind dos grupos visíveis. Curso com
    // módulos → "N módulos"; curso plano (classes) → "N aulas" (mas como já
    // mostramos contagem de aulas adiante, fala em "turmas" pra evitar
    // duplicação semântica). Disciplinas raras → "N disciplinas".
    sizeLabel() {
      return this.course.totalSize ? formatBytes(this.course.totalSize) : '';
    },
    groupsUnitLabel() {
      const kinds = new Set(this.visibleGroups.map(g => g.kind));
      const count = this.visibleGroupsCount;
      if (kinds.has('discipline')) return count === 1 ? 'disciplina' : 'disciplinas';
      return count === 1 ? 'módulo' : 'módulos';
    },
  },
  methods: {
    learningObjectMatchesFilter(lo) {
      const pos = this.getLocalPosition(this.course.enrollId, lo.id);
      const completed = lo.duration && pos >= lo.duration - 3;
      if (this.filter === 'completed') return !!completed;
      if (this.filter === 'inProgress') return pos > 0 && !completed;
      return true;
    },
    isGroupExpanded(groupId, idx) {
      if (Object.prototype.hasOwnProperty.call(this.explicitExpansion, groupId)) {
        return this.explicitExpansion[groupId];
      }
      // Default: primeiro grupo abre quando o curso é expandido.
      return idx === 0;
    },
    toggleGroup(groupId, idx) {
      const current = this.isGroupExpanded(groupId, idx);
      this.$set(this.explicitExpansion, groupId, !current);
    }
  }
};
</script>

<style scoped lang="scss">
.course-card {
  background: var(--md-surface);
  border: 0.5px solid var(--md-border);
  border-radius: 12px;
  margin: 0 14px 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

// Curso cujo backend revogou acesso — exibido pra que user possa remover,
// mas visualmente desaturado pra sinalizar "não disponível pra acessar".
.course-card--no-access {
  .course-name {
    color: var(--md-text-dim);
  }
}

.course-header-wrap {
  display: flex;
  align-items: stretch;
}

.course-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  padding: 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.course-actions-btn {
  flex-shrink: 0;
  width: 40px;
  background: transparent;
  border: none;
  color: var(--md-text-dim);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;

  &:active { color: var(--md-text); }
}

.course-icon {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  // Ícone do curso sem acesso fica dessaturado pra reforçar visualmente que
  // o conteúdo não está mais acessível, mesmo que a imagem original esteja
  // presente em cache.
  &--no-access {
    filter: grayscale(0.85);
    opacity: 0.65;
  }
}

.no-access-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 0 6px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border-radius: 4px;

  i {
    font-size: 12px;
    line-height: 1;
  }
}

.course-info {
  flex: 1;
  min-width: 0;
}

.course-name {
  margin: 0 0 3px;
  font-size: 14px;
  font-weight: 500;
  color: var(--md-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.course-meta {
  margin: 0 0 8px;
  font-size: 11px;
  color: var(--md-text-dim);
}

.course-meta-size {
  font-weight: 600;
}

.chevron {
  color: var(--md-text-dim);
  font-size: 22px;
  padding: 0 6px;
}

.groups {
  // Espaçamento gerenciado por cada DownloadedGroupRow.
}
</style>
